import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Phone, Mail, Plus, Siren, Trash2, UserCheck } from 'lucide-react';
import { apiClient } from '../../api/client';

const EMPTY = { name: '', phone: '', email: '', role: 'backup' };

export const ResponderRota = () => {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        apiClient.request('/api/safety-rota/responders'),
        apiClient.request('/api/safety-rota/alerts?limit=8').catch(() => ({ alerts: [] })),
      ]);
      setData(r);
      setAlerts(a.alerts || []);
    } catch (e) {
      setErr(e.message || 'Failed to load rota');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      await apiClient.request('/api/safety-rota/responders', { method: 'POST', body: JSON.stringify(form) });
      setForm(EMPTY);
      await load();
    } catch (e2) {
      setErr(e2.message || 'Could not add responder');
    } finally { setSaving(false); }
  };

  const patch = async (id, updates) => {
    await apiClient.request(`/api/safety-rota/responders/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this responder from the rota?')) return;
    await apiClient.request(`/api/safety-rota/responders/${id}`, { method: 'DELETE' });
    await load();
  };

  if (!data) return <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}><Loader2 className="animate-spin" size={18} /></div>;

  return (
    <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="responder-rota-card">
      <div className="flex items-center gap-2 mb-1">
        <Siren size={16} style={{ color: '#DC2626' }} />
        <h3 className="font-bold" style={{ color: '#1A1209' }}>CRITICAL Alert Responder Rota</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: '#78716C' }}>
        Primaries get every safety alert instantly (SMS + email with a one-tap acknowledge link). If nobody acknowledges within {data.escalation_minutes} minutes, backups are escalated to automatically.
      </p>

      {data.using_builtin_default && (
        <div className="text-xs rounded-lg p-3 mb-4" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }} data-testid="rota-default-banner">
          No active primary configured — alerts currently go to the built-in default: <strong>{data.builtin_default.name}</strong> ({data.builtin_default.phone}).
        </div>
      )}

      <div className="space-y-2 mb-4" data-testid="rota-list">
        {data.responders.length === 0 && <p className="text-sm" style={{ color: '#A8A29E' }}>No responders added yet.</p>}
        {data.responders.map((r) => (
          <div key={r.responder_id} className="flex flex-wrap items-center gap-2 rounded-lg p-3" style={{ background: '#FAFAF8', border: '1px solid #EEE', opacity: r.active ? 1 : 0.55 }} data-testid={`rota-row-${r.responder_id}`}>
            <UserCheck size={15} style={{ color: r.role === 'primary' ? '#DC2626' : '#D97706' }} />
            <span className="font-semibold text-sm" style={{ color: '#1A1209' }}>{r.name}</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: r.role === 'primary' ? '#FEE2E2' : '#FEF3C7', color: r.role === 'primary' ? '#B91C1C' : '#92400E' }}>{r.role}</span>
            {r.phone && <span className="text-xs flex items-center gap-1" style={{ color: '#57534E' }}><Phone size={11} /> {r.phone}</span>}
            {r.email && <span className="text-xs flex items-center gap-1" style={{ color: '#57534E' }}><Mail size={11} /> {r.email}</span>}
            <span className="flex-1" />
            <button onClick={() => patch(r.responder_id, { role: r.role === 'primary' ? 'backup' : 'primary' })} className="text-xs underline" style={{ color: '#7B4FD8' }} data-testid={`rota-role-toggle-${r.responder_id}`}>
              make {r.role === 'primary' ? 'backup' : 'primary'}
            </button>
            <button onClick={() => patch(r.responder_id, { active: !r.active })} className="text-xs underline" style={{ color: '#57534E' }} data-testid={`rota-active-toggle-${r.responder_id}`}>
              {r.active ? 'deactivate' : 'activate'}
            </button>
            <button onClick={() => remove(r.responder_id)} data-testid={`rota-delete-${r.responder_id}`}><Trash2 size={14} style={{ color: '#DC2626' }} /></button>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="grid grid-cols-2 lg:grid-cols-5 gap-2 items-end" data-testid="rota-add-form">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" required className="border rounded-lg px-3 py-2 text-sm" data-testid="rota-add-name" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 7..." className="border rounded-lg px-3 py-2 text-sm" data-testid="rota-add-phone" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@…" type="email" className="border rounded-lg px-3 py-2 text-sm" data-testid="rota-add-email" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border rounded-lg px-2 py-2 text-sm bg-white" data-testid="rota-add-role">
          <option value="primary">Primary</option>
          <option value="backup">Backup</option>
        </select>
        <button type="submit" disabled={saving} className="flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: '#7B4FD8' }} data-testid="rota-add-submit">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>
      {err && <p className="text-xs mt-2" style={{ color: '#DC2626' }} data-testid="rota-error">{err}</p>}

      {alerts.length > 0 && (
        <div className="mt-5" data-testid="rota-recent-alerts">
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#78716C' }}>Recent alert acknowledgements</p>
          <div className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs rounded px-2 py-1.5" style={{ background: '#FAFAF8' }}>
                <span style={{ color: '#57534E' }}>{new Date(a.created_at).toLocaleString()}</span>
                <span className="font-semibold" style={{ color: '#1A1209' }}>score {a.meta?.crisis_score ?? '?'}</span>
                {a.acknowledged_at
                  ? <span className="font-bold" style={{ color: '#16A34A' }}>ACK {new Date(a.acknowledged_at).toLocaleTimeString()}</span>
                  : <span className="font-bold" style={{ color: '#DC2626' }}>UNACKNOWLEDGED</span>}
                {a.escalated_at && <span className="font-bold" style={{ color: '#D97706' }}>ESCALATED → {(a.escalated_to || []).join(', ')}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponderRota;
